'use client';

import { CloseIcon } from '@insta-club/components/icons';
import { Button } from '@insta-club/ui';
import Image from 'next/image';
import { useEffect, useState } from 'react';

interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>;
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const PWAInstallModal = () => {
    const [showModal, setShowModal] = useState(false);
    const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
    const [canShowInstallButton, setCanShowInstallButton] = useState(false);

    useEffect(() => {
        const modalDismissed = localStorage.getItem('pwa-modal-dismissed');

        const isInstalled =
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true;

        // Check if device is iOS or Android mobile
        const isIOS = /(iPhone|iPad|iPod)/.test(navigator.userAgent);
        const isAndroid = /android/i.test(navigator.userAgent);
        const isMobileDevice = isIOS || isAndroid;

        console.log('Is Installed:', isInstalled);
        console.log('Modal Dismissed:', modalDismissed);
        console.log('Is Mobile Device (iOS or Android):', isMobileDevice);

        if (!isInstalled && !modalDismissed && isMobileDevice) {
            setShowModal(true);
        }

        const handler = (e: Event) => {
            e.preventDefault();
            console.log('beforeinstallprompt fired');
            setDeferredPrompt(e as BeforeInstallPromptEvent);
            setCanShowInstallButton(true);
        };

        window.addEventListener('beforeinstallprompt', handler);

        return () => {
            window.removeEventListener('beforeinstallprompt', handler);
        };
    }, []);

    const handleInstall = async () => {
        if (!deferredPrompt) {
            alert('لطفاً از منوی مرورگر خود گزینه "نصب" یا "Add to Home Screen" را انتخاب کنید.');
            return;
        }

        deferredPrompt.prompt();

        const { outcome } = await deferredPrompt.userChoice;

        if (outcome === 'accepted') {
            console.log('کاربر PWA را نصب کرد');
        }

        setDeferredPrompt(null);
        setShowModal(false);
        localStorage.setItem('pwa-modal-dismissed', 'true');
    };

    const handleDismiss = () => {
        setShowModal(false);
        localStorage.setItem('pwa-modal-dismissed', 'true');
    };

    if (!showModal) return null;

    return (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm'>
            <div className='animate-in fade-in slide-in-from-bottom-4 relative w-full max-w-md rounded-2xl bg-white p-6 shadow-xl duration-300'>
                <button
                    onClick={handleDismiss}
                    className='absolute top-4 right-4 rounded-full p-1 transition-colors hover:bg-gray-100'
                    aria-label='بستن'
                >
                    <CloseIcon className='h-4 w-4 text-gray-500' />
                </button>

                <div className='text-center'>
                    <div className='mx-auto mb-4 h-16 w-16'>
                        <Image src={'/icons/ic-192.png'} width={64} height={64} alt='pwa-icon' />
                    </div>

                    <p className='mb-6 text-gray-600'>
                        برای دسترسی سریع‌تر و تجربه بهتر، اپلیکیشن را روی دستگاه خود نصب کنید.
                    </p>

                    <div className='mb-6 text-sm text-gray-600'>
                        {!/(iPhone|iPad|iPod)/.test(navigator.userAgent) && (
                            <div className='mb-4 rounded-lg bg-blue-50 p-3 text-right'>
                                <p className='mb-2 font-semibold'>راهنمای نصب در Android:</p>
                                <p className='text-xs'>
                                    روی دکمه منو
                                    <span className='mx-1 inline-block'>
                                        <svg
                                            className='inline h-3 w-3'
                                            fill='currentColor'
                                            xmlns='http://www.w3.org/2000/svg'
                                            version='1.1'
                                            id='Capa_1'
                                            x='0px'
                                            y='0px'
                                            viewBox='0 0 512 512'
                                            // style='enable-background:new 0 0 512 512;'
                                            width='512'
                                            height='512'
                                        >
                                            <g>
                                                <circle cx='256' cy='53.333' r='53.333' />
                                                <circle cx='256' cy='256' r='53.333' />
                                                <circle cx='256' cy='458.667' r='53.333' />
                                            </g>
                                        </svg>
                                    </span>
                                    کلیک کنید سپس
                                    {' "Add to Home Screen" '}
                                    یا افزودن به صفحه اصلی
                                    <span className='mx-1 inline-block'>
                                        <svg
                                            fill='currentColor'
                                            className='inline h-3 w-3'
                                            xmlns='http://www.w3.org/2000/svg'
                                            id='Layer_1'
                                            data-name='Layer 1'
                                            viewBox='0 0 24 24'
                                        >
                                            <path d='m19,1h-3c-.553,0-1,.448-1,1s.447,1,1,1h3c1.654,0,3,1.346,3,3v7H2v-6.978c0-.804.314-1.56.885-2.127.566-.563,1.317-.873,2.115-.873h.017l2.978.017h.006c.55,0,.997-.444,1-.995.003-.552-.442-1.002-.994-1.005l-2.978-.017h-.028c-1.331,0-2.582.516-3.525,1.455-.951.945-1.475,2.204-1.475,3.545v7.978c0,2.757,2.243,5,5,5h6v2h-4c-.553,0-1,.448-1,1s.447,1,1,1h10c.553,0,1-.448,1-1s-.447-1-1-1h-4v-2h6c2.757,0,5-2.243,5-5V6c0-2.757-2.243-5-5-5Zm0,16H5c-1.302,0-2.402-.839-2.816-2h19.631c-.414,1.161-1.514,2-2.816,2Zm-8-8.58V1c0-.552.447-1,1-1s1,.448,1,1v7.421l2.319-2.154c.403-.375,1.038-.354,1.413.053.376.405.353,1.037-.052,1.413l-2.613,2.426c-.561.561-1.308.843-2.058.843-.76,0-1.523-.289-2.104-.869l-2.587-2.4c-.404-.376-.428-1.008-.052-1.413.374-.406,1.009-.428,1.413-.053l2.319,2.153Z' />
                                        </svg>
                                    </span>
                                    را انتخاب کنید
                                </p>
                            </div>
                        )}
                        {/(iPhone|iPad|iPod)/.test(navigator.userAgent) && (
                            <div className='rounded-lg bg-blue-50 p-3 text-right'>
                                <p className='mb-2 font-semibold'>راهنمای نصب در IOS:</p>
                                <p className='text-xs'>
                                    روی دکمه Share
                                    <span className='mx-1 inline-block'>
                                        <svg
                                            className='inline h-3 w-3'
                                            fill='currentColor'
                                            xmlns='http://www.w3.org/2000/svg'
                                            id='Layer_1'
                                            data-name='Layer 1'
                                            viewBox='0 0 24 24'
                                            width='512'
                                            height='512'
                                        >
                                            <path d='M5.279,6.693c-.383-.398-.371-1.031,.027-1.414L9.893,.865c1.105-1.106,3.095-1.12,4.229,.014l4.572,4.4c.398,.383,.41,1.016,.027,1.414-.196,.204-.458,.307-.721,.307-.25,0-.5-.093-.693-.279L13,2.575v13.425c0,.552-.448,1-1,1s-1-.448-1-1V2.575L6.693,6.721c-.397,.383-1.029,.371-1.414-.027Zm15.721,1.307c-.552,0-1,.448-1,1v10c0,1.654-1.346,3-3,3H7c-1.654,0-3-1.346-3-3V9c0-.552-.448-1-1-1s-1,.448-1,1v10c0,2.757,2.243,5,5,5h10c2.757,0,5-2.243,5-5V9c0-.552-.448-1-1-1Z' />
                                        </svg>
                                    </span>
                                    کلیک کنید سپس
                                    {' "Add to Home Screen" '}
                                    <span className='mx-1 inline-block'>
                                        <svg
                                            className='inline h-3 w-3'
                                            xmlns='http://www.w3.org/2000/svg'
                                            id='Layer_1'
                                            data-name='Layer 1'
                                            viewBox='0 0 24 24'
                                            width='512'
                                            height='512'
                                        >
                                            <path d='M17,12c0,.553-.448,1-1,1h-3v3c0,.553-.448,1-1,1s-1-.447-1-1v-3h-3c-.552,0-1-.447-1-1s.448-1,1-1h3v-3c0-.553,.448-1,1-1s1,.447,1,1v3h3c.552,0,1,.447,1,1Zm7-7v14c0,2.757-2.243,5-5,5H5c-2.757,0-5-2.243-5-5V5C0,2.243,2.243,0,5,0h14c2.757,0,5,2.243,5,5Zm-2,0c0-1.654-1.346-3-3-3H5c-1.654,0-3,1.346-3,3v14c0,1.654,1.346,3,3,3h14c1.654,0,3-1.346,3-3V5Z' />
                                        </svg>
                                    </span>
                                    را انتخاب کنید
                                </p>
                            </div>
                        )}
                    </div>

                    <div className='flex gap-3'>
                        {canShowInstallButton ? (
                            <Button fullWidth onClick={handleInstall} className='w-1/2' size='sm'>
                                نصب اپلیکیشن
                            </Button>
                        ) : (
                            !/(iPhone|iPad|iPod)/.test(navigator.userAgent) && (
                                <Button fullWidth onClick={handleInstall} className='w-1/2' size='sm'>
                                    نصب اپلیکیشن
                                </Button>
                            )
                        )}
                        {/(iPhone|iPad|iPod)/.test(navigator.userAgent) ? (
                            <Button onClick={handleDismiss} size='sm' fullWidth>
                                متوجه شدم
                            </Button>
                        ) : (
                            <Button onClick={handleDismiss} size='sm' fullWidth className='w-1/2' color='gray'>
                                متوجه شدم
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PWAInstallModal;

